import { clients } from "@/data/clients";

/**
 * "בין לקוחותינו" — premium glassmorphism marquee.
 * Floats over the Hero/Video boundary, infinite seamless loop, no JS.
 */
const ClientsMarquee = () => {
  // Build a single base row that is always wide enough for any viewport,
  // then render it twice — the second copy guarantees a seamless -50% loop.
  const MIN_ITEMS = 14;
  const repeats = Math.max(1, Math.ceil(MIN_ITEMS / clients.length));
  const baseRow = Array.from({ length: repeats }).flatMap(() => clients);

  const renderRow = (keyPrefix: string) =>
    baseRow.map((c, i) => (
      <li key={`${keyPrefix}-${c.name}-${i}`} className="clients-marquee-item">
        <div className="clients-logo-card group/card">
          <div className="clients-logo-card__glow" aria-hidden />
          <img
            src={c.src}
            alt={c.name}
            loading="lazy"
            decoding="async"
            className="relative z-10 max-h-12 md:max-h-14 w-auto object-contain"
          />
          <span className="clients-logo-card__name">{c.name}</span>
        </div>
      </li>
    ));

  return (
    <section
      aria-label="בין לקוחותינו"
      dir="rtl"
      className="relative z-20 -mt-12 md:-mt-20"
    >
      <div className="container px-4">
        <div className="clients-shell relative overflow-hidden rounded-2xl md:rounded-3xl">
          {/* Animated aurora background */}
          <div aria-hidden className="clients-aurora" />
          <div aria-hidden className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(0_85%_55%/0.18),transparent_60%)]" />

          <div className="relative px-5 py-7 md:px-10 md:py-9">
            {/* Header strip: title + live stats */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-6 mb-6 md:mb-8">
              <div className="flex items-center gap-3">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-accent opacity-75 animate-ping" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-accent" />
                </span>
                <span className="text-[11px] md:text-xs font-bold tracking-[0.4em] text-accent uppercase">
                  Trusted Partners
                </span>
              </div>

              <h2 className="text-2xl md:text-4xl font-black text-primary-foreground text-center">
                בין <span className="bg-gradient-to-r from-accent to-[hsl(0_85%_60%)] bg-clip-text text-transparent">לקוחותינו</span>
              </h2>

              <div className="hidden md:flex items-center gap-6 text-primary-foreground/90">
                <div className="text-center">
                  <div className="text-2xl font-black leading-none text-accent">+50</div>
                  <div className="text-[10px] tracking-widest uppercase opacity-70 mt-1">פרויקטים</div>
                </div>
                <div className="h-8 w-px bg-primary-foreground/20" />
                <div className="text-center">
                  <div className="text-2xl font-black leading-none text-accent">+13000</div>
                  <div className="text-[10px] tracking-widest uppercase opacity-70 mt-1">דיירים</div>
                </div>
              </div>
            </div>

            {/* Marquee track */}
            <div className="relative" dir="ltr">
              {/* Edge fades */}
              <div aria-hidden className="clients-fade clients-fade--left" />
              <div aria-hidden className="clients-fade clients-fade--right" />

              <div className="clients-marquee">
                <div className="clients-marquee-track">
                  <ul className="clients-marquee-group">{renderRow("a")}</ul>
                  <ul className="clients-marquee-group" aria-hidden>
                    {renderRow("b")}
                  </ul>
                </div>
              </div>
            </div>

            {/* Mobile stats */}
            <div className="md:hidden mt-6 flex items-center justify-center gap-8 text-primary-foreground/90">
              <div className="text-center">
                <div className="text-xl font-black leading-none text-accent">+50</div>
                <div className="text-[10px] tracking-widest uppercase opacity-70 mt-1">פרויקטים</div>
              </div>
              <div className="h-7 w-px bg-primary-foreground/20" />
              <div className="text-center">
                <div className="text-xl font-black leading-none text-accent">+13000</div>
                <div className="text-[10px] tracking-widest uppercase opacity-70 mt-1">דיירים</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ClientsMarquee;
